import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, session } from '../../shell/bridge';
import { SkeletonBlock } from '../../ui/Skeleton';
import './questions.css';

interface QuestionDay {
  questionId: string;
  dateKey: string;
  prompt: string;
  state: 'pending' | 'revealed';
  deadlineAt: number;
  revealedAt: number | null;
  answeredBy: string[];
}

interface QuestionAnswer {
  questionId: string;
  memberId: string;
  text: string;
}

interface RevealPayload {
  question: QuestionDay;
  answers: QuestionAnswer[];
}

/**
 * Daily question panel — ported from `questions/ui.ts` and given a screen
 * pass (design review): the question is the hero in a serif card, both
 * partners' answered/unanswered state is explicit, and the submit stays
 * disabled until there is real text.
 */
export function QuestionsPanel({ members }: { members: { memberId: string; displayName: string }[] }) {
  const me = session();

  const [question, setQuestion] = useState<QuestionDay | null>(null);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [history, setHistory] = useState<RevealPayload[] | null>(null);
  const [answered, setAnswered] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const data = await api<{ reveals: RevealPayload[] }>('/api/questions/history');
      setHistory(data.reveals ?? []);
    } catch {
      setHistory([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await api<{ question: QuestionDay }>('/api/questions/today');
      const qd = data.question;
      setQuestion(qd);

      if (qd.state === 'revealed') {
        try {
          const payload = await api<RevealPayload>(`/api/questions/${qd.questionId}`);
          setReveal(payload);
        } catch {
          setReveal(null);
        }
      }
      void loadHistory();
    } catch {
      setFailed(true);
    }
  }, [loadHistory]);

  useEffect(() => {
    if (me) void load();
  }, [me, load]);

  if (!me) return <p className="q-guard">Please log in first.</p>;
  if (failed) return <p className="q-guard">Failed to load daily question.</p>;
  if (!question) {
    return (
      <div className="q-panel" aria-busy="true">
        <SkeletonBlock lines={4} />
      </div>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending || answered) return;
    const text = draft.trim();
    if (!text) return;

    setSending(true);
    try {
      const result = await api<{ question: QuestionDay; reveal: RevealPayload | null }>(
        `/api/questions/${question.questionId}/answer`,
        { method: 'POST', body: { text } },
      );
      if (result.reveal) {
        setReveal(result.reveal);
        void loadHistory();
      } else {
        setAnswered(true);
        setDraft('');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSending(false);
    }
  };

  const canSubmit = draft.trim().length > 0 && !sending && !answered;

  return (
    <div className="q-panel">
      {reveal ? (
        <>
          <div className="pear-q-eyebrow">Revealed together</div>
          <div className="pear-q-prompt pear-q-hero">{reveal.question.prompt}</div>
          <div className="pear-q-answers-row pear-q-section">
            {reveal.answers.map(ans => {
              const isMine = me.memberId === ans.memberId;
              const pname = members.find(m => m.memberId === ans.memberId)?.displayName ?? 'Your partner';
              return (
                <div className="pear-q-answer-card pear-q-answer" key={ans.memberId}>
                  <span className="pear-q-role-label">{isMine ? 'You said' : `${pname} said`}</span>
                  <div className="pear-q-answer-text">{ans.text}</div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="pear-q-eyebrow">Today's question</div>
          <div className="pear-q-prompt pear-q-hero">{question.prompt}</div>

          {/* State before action: who has/hasn't answered, then the composer. */}
          <div className="pear-q-status-row" role="status">
            {me.memberId && question.answeredBy.includes(me.memberId) ? (
              <span className="pear-q-chip is-answered">You answered</span>
            ) : (
              <span className="pear-q-chip">You haven't answered yet</span>
            )}
            {(() => {
              const partner = members.find(m => m.memberId !== me.memberId);
              if (!partner) return <span className="pear-q-chip">Waiting on your partner to join</span>;
              const joined = question.answeredBy.includes(partner.memberId);
              return joined ? (
                <span className="pear-q-chip is-answered">{partner.displayName} answered</span>
              ) : (
                <span className="pear-q-chip">{partner.displayName} hasn't answered yet</span>
              );
            })()}
          </div>

          <form className="pear-q-form" onSubmit={submit}>
            <label htmlFor="q-answer">Your answer</label>
            <textarea
              id="q-answer"
              name="answer"
              rows={3}
              placeholder="Type your private answer…"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              disabled={answered}
            />
            <button type="submit" className="pear-q-btn" disabled={!canSubmit}>
              {answered ? 'Locked in — reveals when your partner answers' : sending ? 'Sending…' : 'Submit answer'}
            </button>
          </form>
        </>
      )}

      <div className="pear-q-hist-section">
        <h3>Reveal history</h3>
        <ul className="pear-q-hist-list">
          {history === null && <SkeletonBlock lines={2} />}
          {history !== null && history.length === 0 && <li className="pear-q-hist-empty">No reveals yet.</li>}
          {history?.map(rp => (
            <li className="pear-q-hist-card" key={rp.question.questionId}>
              <div className="pear-q-hist-prompt">{rp.question.prompt}</div>
              <div className="pear-q-hist-answers">
                {rp.answers.map(ans => (
                  <span className="pear-q-hist-answer" key={ans.memberId}>
                    {ans.text}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}